import { useRef, useEffect, useCallback } from 'react';
import { CANVAS_CONFIG } from '@beat-games/game-config';
import { useGameStore } from '../../stores/gameStore';
import { useGameEngine } from '../../hooks/useGameEngine';
import { useGameAudio } from '../../hooks/useGameAudio';

interface GameCanvasProps {
  onPurchaseClick?: () => void;
}

// Danger zone thresholds (as percentage of radius from center)
const DANGER_ZONE_START = 0.8; // Warning starts at 80% from center
const DANGER_ZONE_FULL = 0.95; // Full danger at 95% from center

// Colors for danger states
const COLOR_SAFE = { r: 255, g: 255, b: 255 }; // White
const COLOR_WARNING = { r: 251, g: 146, b: 60 }; // Orange
const COLOR_DANGER = { r: 239, g: 68, b: 68 }; // Red

// Helper to interpolate between two colors
function lerpColor(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number },
  t: number
): { r: number; g: number; b: number } {
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * t),
    g: Math.round(color1.g + (color2.g - color1.g) * t),
    b: Math.round(color1.b + (color2.b - color1.b) * t),
  };
}

// Calculate danger level (0 = safe, 1 = at edge, >1 = outside)
function calculateDangerLevel(
  cursorX: number,
  cursorY: number,
  circleX: number,
  circleY: number,
  radius: number
): number {
  const dx = cursorX - circleX;
  const dy = cursorY - circleY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  return distance / radius;
}

// Get border color based on danger level
function getDangerColor(dangerLevel: number): { r: number; g: number; b: number } {
  if (dangerLevel > 1) {
    // Outside circle - red
    return COLOR_DANGER;
  } else if (dangerLevel > DANGER_ZONE_FULL) {
    // Full danger zone (95-100%) - orange to red
    const t = (dangerLevel - DANGER_ZONE_FULL) / (1 - DANGER_ZONE_FULL);
    return lerpColor(COLOR_WARNING, COLOR_DANGER, t);
  } else if (dangerLevel > DANGER_ZONE_START) {
    // Warning zone (80-95%) - white to orange
    const t = (dangerLevel - DANGER_ZONE_START) / (DANGER_ZONE_FULL - DANGER_ZONE_START);
    return lerpColor(COLOR_SAFE, COLOR_WARNING, t);
  }
  // Safe zone - white
  return COLOR_SAFE;
}

export default function GameCanvas({ onPurchaseClick }: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { phase, circle, cursorPosition, isInsideCircle, connectionStatus, errorCode, errorMessage, setCursorPosition } =
    useGameStore();
  const { connectToGame } = useGameEngine();
  const { initializeAudio } = useGameAudio();

  // Handle mouse/touch movement
  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      setCursorPosition({ x, y });
    },
    [setCursorPosition]
  );

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;

    const render = () => {
      // Clear canvas
      ctx.fillStyle = CANVAS_CONFIG.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw subtle grid pattern for arena feel
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.02)';
      ctx.lineWidth = 1;
      const gridSize = 40;
      for (let x = gridSize; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = gridSize; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Draw circle if in playing state
      if (circle && (phase === 'playing' || phase === 'countdown')) {
        // Calculate danger level based on cursor position
        let dangerLevel = 0;
        if (cursorPosition && phase === 'playing') {
          dangerLevel = calculateDangerLevel(
            cursorPosition.x,
            cursorPosition.y,
            circle.x,
            circle.y,
            circle.radius
          );
        }

        // Get colors based on danger level
        const dangerColor = getDangerColor(dangerLevel);
        const isInDanger = dangerLevel > DANGER_ZONE_START;
        const isOutside = dangerLevel > 1;

        // Pulsing effect for danger zone (synced with audio at ~8Hz)
        const pulseIntensity = isInDanger && !isOutside
          ? 0.5 + Math.sin(Date.now() * 0.016 * Math.PI) * 0.5 // 8Hz pulse
          : 1;

        // Outer glow effect - changes color based on danger
        const glowGradient = ctx.createRadialGradient(
          circle.x,
          circle.y,
          circle.radius,
          circle.x,
          circle.y,
          circle.radius * 1.8
        );
        const glowOpacity = isInDanger ? 0.12 * pulseIntensity : 0.08;
        glowGradient.addColorStop(0, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${glowOpacity})`);
        glowGradient.addColorStop(0.5, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${glowOpacity * 0.4})`);
        glowGradient.addColorStop(1, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, 0)`);
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius * 1.8, 0, Math.PI * 2);
        ctx.fill();

        // Extra pulsing glow ring when in danger zone
        if (isInDanger && !isOutside) {
          const pulseRadius = circle.radius * (1.1 + pulseIntensity * 0.15);
          ctx.strokeStyle = `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${0.3 * pulseIntensity})`;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(circle.x, circle.y, pulseRadius, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Main circle border - gradient color based on danger
        ctx.strokeStyle = `rgb(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
        ctx.stroke();

        // Inner fill - tinted based on danger level
        const fillGradient = ctx.createRadialGradient(
          circle.x,
          circle.y,
          0,
          circle.x,
          circle.y,
          circle.radius
        );
        const fillOpacityCenter = isOutside ? 0.15 : (isInDanger ? 0.1 : 0.08);
        const fillOpacityEdge = isOutside ? 0.05 : (isInDanger ? 0.04 : 0.02);
        fillGradient.addColorStop(0, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${fillOpacityCenter})`);
        fillGradient.addColorStop(1, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${fillOpacityEdge})`);
        ctx.fillStyle = fillGradient;
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw cursor indicator
      if (cursorPosition && phase === 'playing') {
        // Calculate cursor danger color (reuse from circle calculation if available)
        let cursorDangerLevel = 0;
        if (circle) {
          cursorDangerLevel = calculateDangerLevel(
            cursorPosition.x,
            cursorPosition.y,
            circle.x,
            circle.y,
            circle.radius
          );
        }
        const cursorColor = getDangerColor(cursorDangerLevel);

        // Cursor glow
        const cursorGlow = ctx.createRadialGradient(
          cursorPosition.x,
          cursorPosition.y,
          0,
          cursorPosition.x,
          cursorPosition.y,
          12
        );
        cursorGlow.addColorStop(0, `rgba(${cursorColor.r}, ${cursorColor.g}, ${cursorColor.b}, 0.3)`);
        cursorGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = cursorGlow;
        ctx.beginPath();
        ctx.arc(cursorPosition.x, cursorPosition.y, 12, 0, Math.PI * 2);
        ctx.fill();

        // Cursor dot
        ctx.fillStyle = `rgb(${cursorColor.r}, ${cursorColor.g}, ${cursorColor.b})`;
        ctx.beginPath();
        ctx.arc(cursorPosition.x, cursorPosition.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw waiting state
      if (phase === 'waiting') {
        // Center visual element
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        // Check if we're in connecting state (loading)
        const isConnecting = connectionStatus === 'connecting' || connectionStatus === 'connected';

        if (isConnecting) {
          // === GET READY / Loading State ===
          const time = Date.now();

          // Pulsing base radius
          const pulse = Math.sin(time / 300) * 8;
          const baseRadius = 70 + pulse;

          // Spinning arc segments
          const spinSpeed = time / 400;
          const numArcs = 3;

          for (let i = 0; i < numArcs; i++) {
            const arcOffset = (i / numArcs) * Math.PI * 2;
            const arcStart = spinSpeed + arcOffset;
            const arcLength = Math.PI * 0.4;

            // Each arc has different opacity for depth effect
            const opacity = 0.3 + (i * 0.15);
            ctx.strokeStyle = `rgba(0, 255, 136, ${opacity})`;
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(centerX, centerY, baseRadius + (i * 12), arcStart, arcStart + arcLength);
            ctx.stroke();
          }

          // Inner spinning ring (opposite direction)
          ctx.strokeStyle = 'rgba(0, 255, 136, 0.2)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, baseRadius - 15, -spinSpeed * 1.5, -spinSpeed * 1.5 + Math.PI * 1.2);
          ctx.stroke();

          // Center glow
          const glowGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius);
          glowGradient.addColorStop(0, 'rgba(0, 255, 136, 0.15)');
          glowGradient.addColorStop(0.5, 'rgba(0, 255, 136, 0.05)');
          glowGradient.addColorStop(1, 'transparent');
          ctx.fillStyle = glowGradient;
          ctx.beginPath();
          ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
          ctx.fill();

          // Pulsing outer ring
          const outerPulse = Math.sin(time / 200) * 0.3 + 0.7;
          ctx.strokeStyle = `rgba(0, 255, 136, ${0.1 * outerPulse})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(centerX, centerY, baseRadius + 40, 0, Math.PI * 2);
          ctx.stroke();

          // "GET READY" text with glow effect
          ctx.shadowColor = '#00ff88';
          ctx.shadowBlur = 20;
          ctx.fillStyle = '#00ff88';
          ctx.font = '700 24px "JetBrains Mono", monospace';
          ctx.textAlign = 'center';
          ctx.fillText('GET READY', centerX, centerY - 5);

          // Reset shadow
          ctx.shadowBlur = 0;

          // Animated dots
          const dotCount = 3;
          const visibleDots = Math.floor((time / 400) % (dotCount + 1));
          let dots = '';
          for (let i = 0; i < visibleDots; i++) dots += '.';

          ctx.fillStyle = 'rgba(0, 255, 136, 0.6)';
          ctx.font = '400 14px "JetBrains Mono", monospace';
          ctx.fillText('Preparing arena' + dots, centerX, centerY + 25);

        } else {
          // === Normal Waiting State (Click to Start / Error States) ===

          // Breathing circle animation
          const breathe = Math.sin(Date.now() / 800) * 3;
          const baseRadius = 60 + breathe;

          // Determine colors based on error state
          const isError = connectionStatus === 'error';
          const isCreditsError = errorCode === 'NO_CREDITS';
          const isBalanceError = errorCode === 'INSUFFICIENT_BALANCE';
          const isRateLimited = errorCode === 'RATE_LIMITED';
          const isNotQualified = errorCode === 'NOT_QUALIFIED';
          const isEliminated = errorCode === 'ELIMINATED';

          // Color scheme based on error type
          let primaryColor = 'rgba(255, 255, 255, 0.2)';
          let glowColor = 'rgba(255, 255, 255, 0.05)';
          let accentColor = '#666666';

          if (isError) {
            if (isCreditsError) {
              primaryColor = 'rgba(251, 191, 36, 0.3)'; // Amber
              glowColor = 'rgba(251, 191, 36, 0.08)';
              accentColor = '#fbbf24';
            } else if (isBalanceError) {
              primaryColor = 'rgba(249, 115, 22, 0.3)'; // Orange
              glowColor = 'rgba(249, 115, 22, 0.08)';
              accentColor = '#f97316';
            } else if (isRateLimited) {
              primaryColor = 'rgba(139, 92, 246, 0.3)'; // Purple
              glowColor = 'rgba(139, 92, 246, 0.08)';
              accentColor = '#8b5cf6';
            } else if (isNotQualified || isEliminated) {
              primaryColor = 'rgba(239, 68, 68, 0.3)'; // Red
              glowColor = 'rgba(239, 68, 68, 0.08)';
              accentColor = '#ef4444';
            } else {
              primaryColor = 'rgba(239, 68, 68, 0.3)'; // Red
              glowColor = 'rgba(239, 68, 68, 0.08)';
              accentColor = '#ef4444';
            }
          }

          // Outer ring
          ctx.strokeStyle = primaryColor.replace('0.3', '0.1').replace('0.2', '0.1');
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(centerX, centerY, baseRadius + 20, 0, Math.PI * 2);
          ctx.stroke();

          // Middle ring
          ctx.strokeStyle = primaryColor;
          ctx.beginPath();
          ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
          ctx.stroke();

          // Inner glow
          const waitGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius);
          waitGlow.addColorStop(0, glowColor);
          waitGlow.addColorStop(1, 'transparent');
          ctx.fillStyle = waitGlow;
          ctx.beginPath();
          ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
          ctx.fill();

          // Draw error-specific icons
          if (isError && isCreditsError) {
            // Credit/ticket icon
            ctx.strokeStyle = accentColor;
            ctx.lineWidth = 1.5;
            const iconSize = 16;
            const iconY = centerY - 45;

            // Ticket shape
            ctx.beginPath();
            ctx.roundRect(centerX - iconSize, iconY - iconSize/2, iconSize * 2, iconSize, 3);
            ctx.stroke();

            // Ticket notches
            ctx.beginPath();
            ctx.arc(centerX - iconSize, iconY, 3, 0, Math.PI * 2);
            ctx.arc(centerX + iconSize, iconY, 3, 0, Math.PI * 2);
            ctx.fillStyle = CANVAS_CONFIG.backgroundColor;
            ctx.fill();
          } else if (isError && isBalanceError) {
            // Wallet icon
            ctx.strokeStyle = accentColor;
            ctx.lineWidth = 1.5;
            const iconY = centerY - 45;

            ctx.beginPath();
            ctx.roundRect(centerX - 12, iconY - 8, 24, 16, 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(centerX + 6, iconY, 3, 0, Math.PI * 2);
            ctx.stroke();
          } else if (isError && isRateLimited) {
            // Clock icon
            ctx.strokeStyle = accentColor;
            ctx.lineWidth = 1.5;
            const iconY = centerY - 45;

            ctx.beginPath();
            ctx.arc(centerX, iconY, 10, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(centerX, iconY - 5);
            ctx.lineTo(centerX, iconY);
            ctx.lineTo(centerX + 4, iconY + 2);
            ctx.stroke();
          } else if (isError && (isNotQualified || isEliminated)) {
            // X icon for not qualified/eliminated
            ctx.strokeStyle = accentColor;
            ctx.lineWidth = 2;
            const iconY = centerY - 45;
            const iconSize = 10;

            ctx.beginPath();
            ctx.moveTo(centerX - iconSize, iconY - iconSize);
            ctx.lineTo(centerX + iconSize, iconY + iconSize);
            ctx.moveTo(centerX + iconSize, iconY - iconSize);
            ctx.lineTo(centerX - iconSize, iconY + iconSize);
            ctx.stroke();

            // Circle around X
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(centerX, iconY, 14, 0, Math.PI * 2);
            ctx.stroke();
          }

          // Status text
          ctx.fillStyle = isError ? accentColor : '#666666';
          ctx.font = '600 13px "JetBrains Mono", monospace';
          ctx.textAlign = 'center';

          let statusText = 'CLICK TO START';
          let subText = '';

          if (isError) {
            if (isCreditsError) {
              statusText = 'NO CREDITS';
              subText = 'Purchase credits to continue';
            } else if (isBalanceError) {
              statusText = 'INSUFFICIENT $BG';
              subText = 'Need 25,000 $BG to play';
            } else if (isRateLimited) {
              statusText = 'COOLDOWN';
              subText = errorMessage || 'Please wait before retrying';
            } else if (isNotQualified) {
              statusText = 'NOT QUALIFIED';
              subText = errorMessage || 'You did not qualify for this phase';
            } else if (isEliminated) {
              statusText = 'ELIMINATED';
              subText = 'You have been eliminated from this season';
            } else {
              statusText = 'CONNECTION FAILED';
              subText = errorMessage || 'Click to retry';
            }
          }

          ctx.fillText(statusText, centerX, centerY + 4);

          // Subtext
          if (subText) {
            ctx.fillStyle = isError ? accentColor + '99' : '#444444';
            ctx.font = '400 11px "JetBrains Mono", monospace';
            ctx.fillText(subText, centerX, centerY + 22);
          }

          // Draw "Get Credits" button for NO_CREDITS error
          if (isError && isCreditsError) {
            const btnWidth = 120;
            const btnHeight = 32;
            const btnX = centerX - btnWidth / 2;
            const btnY = centerY + 40;

            // Button background
            ctx.fillStyle = accentColor;
            ctx.beginPath();
            ctx.roundRect(btnX, btnY, btnWidth, btnHeight, 4);
            ctx.fill();

            // Button text
            ctx.fillStyle = '#000000';
            ctx.font = '600 11px "JetBrains Mono", monospace';
            ctx.fillText('GET CREDITS', centerX, btnY + btnHeight / 2 + 4);
          }
        }
      }

      animationId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [phase, circle, cursorPosition, isInsideCircle, connectionStatus, errorCode, errorMessage]);

  // Click to start game
  const handleClick = async (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Initialize audio on any click (user gesture required for Web Audio API)
    await initializeAudio();

    if (phase === 'waiting' && connectionStatus !== 'connecting') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      // Check if clicking on "Get Credits" button for NO_CREDITS error
      if (connectionStatus === 'error' && errorCode === 'NO_CREDITS') {
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const btnWidth = 120;
        const btnHeight = 32;
        const btnX = centerX - btnWidth / 2;
        const btnY = centerY + 40;

        if (x >= btnX && x <= btnX + btnWidth && y >= btnY && y <= btnY + btnHeight) {
          onPurchaseClick?.();
          return;
        }
      }

      // Don't allow retry for permanent errors (not qualified or eliminated)
      if (connectionStatus === 'error' && (errorCode === 'NOT_QUALIFIED' || errorCode === 'ELIMINATED')) {
        return;
      }

      // Initialize cursor position to click location
      setCursorPosition({ x, y });
      connectToGame();
    }
  };

  // Only hide cursor during active gameplay (countdown or playing)
  const hideCursor = phase === 'playing' || phase === 'countdown';

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_CONFIG.width}
      height={CANVAS_CONFIG.height}
      className={`absolute inset-0 w-full h-full touch-none ${hideCursor ? 'cursor-none' : 'cursor-crosshair'}`}
      style={{ touchAction: 'none' }}
      onPointerMove={handlePointerMove}
      onClick={handleClick}
    />
  );
}
